import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Button,
  message,
} from "antd";
import { platformApi } from "../../../../../api";

const { Item } = Form;
const { Option } = Select;

const AddNumberPlateTransferModal = ({
  open,
  close,
  form,
  createAccess = false,
  initialValues = null,
}) => {
  const { getFieldDecorator, setFieldsValue, validateFields, getFieldValue } = form;

  const [numberPlates, setNumberPlates] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  useEffect(() => {
    getBranch();
  }, []);

  useEffect(() => {
    if (!open || !initialValues || !initialValues.fromBranchId) return;

    const fromId = initialValues.fromBranchId;
    const toId = initialValues.toBranchId;
    const selectedIds = (initialValues.plates || []).map((p) => p.id).filter(Boolean);

    setFieldsValue({
      fromBranch: fromId,
      toBranch: toId,
      numberPlate: selectedIds,
    });

    platformApi
      .post(`/api/numberplate/get`, {
        status: "RECEIVED",
        searchString: "",
        branch: fromId,
      })
      .then((res) => {
        const apiOptions = (res.data.data?.numberPlates || []).map((obj) => ({
          value: obj.id,
          label: obj.registerNo,
        }));
        const suggestionOptions = (initialValues.plates || []).map((p) => ({
          value: p.id,
          label: p.registerNo || p.chassisNo || p.id,
        }));
        const mergedMap = new Map();
        [...apiOptions, ...suggestionOptions].forEach((opt) => {
          if (opt.value) mergedMap.set(opt.value, opt);
        });
        setNumberPlates([...mergedMap.values()]);
        setFieldsValue({
          fromBranch: fromId,
          toBranch: toId,
          numberPlate: selectedIds,
        });
      })
      .catch(() => {
        const suggestionOptions = (initialValues.plates || []).map((p) => ({
          value: p.id,
          label: p.registerNo || p.chassisNo || p.id,
        }));
        setNumberPlates(suggestionOptions);
        setFieldsValue({
          fromBranch: fromId,
          toBranch: toId,
          numberPlate: selectedIds,
        });
        message.error("Failed to fetch registration numbers");
      });
  }, [open, initialValues]);

  const getBranch = () => {
    platformApi.get("/api/user/branch").then((res) => {
      setBranchOptions(res.data.data);
    });
  };

  const getNumberPlates = (search = "", branch) => {
    platformApi
      .post(`/api/numberplate/get`, {
        status: "RECEIVED",
        searchString: search,
        branch,
      })
      .then((res) => {
        const options = (res.data.data?.numberPlates || []).map((obj) => ({
          value: obj.id,
          label: obj.registerNo,
        }));
        setNumberPlates(options);
      })
      .catch(() => {
        message.error("Failed to fetch registration numbers");
      });
  };

  const handleFromBranchChange = (value) => {
    // Reset dependent fields
    setFieldsValue({ toBranch: undefined, numberPlate: [] });
    setNumberPlates([]);

    if (value) {
      getNumberPlates("", value);
      setFieldsValue({
        fromBranch: value,
      });
    }
  };

  const handleSubmit = () => {
    if (!createAccess) {
      message.error("You do not have permission to submit number plate transfers.");
      return;
    }
    validateFields((err, values) => {
      if (err) return;

      const payload = {
        fromBranch: values.fromBranch,
        toBranch: values.toBranch,
        id: values.numberPlate, // array of selected plates
      };

      platformApi
        .post(`/api/inventoryTransfers/numberPlate`, payload)
        .then((res) => {
          message.success(`Number Plate(s) transferred successfully`);
          window.dispatchEvent(new Event("pendingNumberPlateTransfersUpdated"));
          close();
        })
        .catch(() => {
          message.error(`Error in transferring Number Plate(s)`);
        });
    });
  };

  const fromBranchValue = getFieldValue("fromBranch");

  return (
    <Modal
      title={initialValues ? "Auto Number Plate Transfer" : "Add Number Plate Transfer"}
      visible={open}
      onCancel={close}
      footer={null}
      width={500}
      destroyOnClose
    >
      <Form layout="vertical">
        <Item label="From Branch">
          {getFieldDecorator("fromBranch", {
            rules: [{ required: true, message: "Please select From Branch" }],
          })(
            <Select
              placeholder="Select From Branch"
              onChange={handleFromBranchChange}
              showSearch
              optionFilterProp="children"
            >
              {branchOptions?.map((b) => (
                <Option key={b.id} value={b.id}>
                  {b.name}
                </Option>
              ))}
            </Select>
          )}
        </Item>

        <Item label="To Branch">
          {getFieldDecorator("toBranch", {
            rules: [{ required: true, message: "Please select To Branch" }],
          })(
            <Select
              placeholder="Select To Branch"
              showSearch
              optionFilterProp="children"
              disabled={!fromBranchValue}
            >
              {branchOptions
                ?.filter((b) => b.id !== fromBranchValue)
                .map((b) => (
                  <Option key={b.id} value={b.id}>
                    {b.name}
                  </Option>
                ))}
            </Select>
          )}
        </Item>

        <Item label="Number Plates">
          {getFieldDecorator("numberPlate", {
            rules: [{ required: true, message: "Please select Number Plate(s)" }],
          })(
            <Select
              mode="multiple"
              placeholder="Select Number Plate(s)"
              disabled={!numberPlates.length}
              showSearch
              optionFilterProp="children"
            >
              {numberPlates?.map((np) => (
                <Option key={np.value} value={np.value}>
                  {np.label}
                </Option>
              ))}
            </Select>
          )}
        </Item>

        <Button type="primary" block onClick={handleSubmit} disabled={!createAccess}>
          Transfer Number Plate(s)
        </Button>
      </Form>
    </Modal>
  );
};

export default Form.create()(AddNumberPlateTransferModal);
